import Immutable from 'immutable'
import actionTypes from 'downloads/profile-download-center-action-types'
import {
    PDFDownloadCenterModes,
    PDFDownloadCenterSubModes,
    GLOBAL_DATA_KEY,
    DEFAULT_FIRST_PAGE,
    LONG_PDF_HEADER_TITLE_LENGTH,
    XL_PDF_HEADER_TITLE_LENGTH,
    XXL_PDF_HEADER_TITLE_LENGTH,
    XXXL_PDF_HEADER_TITLE_LENGTH,
} from 'downloads/constants/ProfileDownloadCenterConstants'
import templateActionTypes from "downloads/template-manager-action-types"

/**
 * State organization based on best practices for normalizing state shape:
 *  - http://redux.js.org/docs/recipes/reducers/NormalizingStateShape.html
 *  - http://redux.js.org/docs/recipes/reducers/UpdatingNormalizedData.html
 *
 * Example State: {
 *      currentPage: 'pageID2',
 *      pages :{
 *          pageID1: [
 *              elementID1,
 *              elementID2,
 *          ],
 *          pageID2: [
 *              elementID3,
 *              elementID4,
 *              elementID5,
 *          ],
 *          ...
 *      },
 *      elementDetails: {
 *          elementID1: {displayElement: 'PersonBio', height: 2, ...},
 *          elementID2: {displayElement: 'PersonStats', height: 4, ...},
 *          elementID3: {displayElement: 'Map', height: 3, ...},
 *          ...
 *      },
 *      ...
 *  }
**/

export const initialBatchSearchifyFilters = () => (
    {
        addedIds: [],
        deletedIds: [],
        querysetList: [],
        searchifyCount: 0,
    }
)

const initialState = Immutable.fromJS({})

export const getInitialState = (kwargs = {}) => initialState.merge(kwargs)

export const profileDownloadCenterReducer = (state = initialState, action) => {
    // console.log(`Dispatching ${action.type}`)

    /* eslint-disable no-case-declarations */
    switch (action.type) {

        case actionTypes.UPDATE_CURRENT_PAGE:
            return state.set('currentPage', action.page)

        case actionTypes.UPDATE_CURRENT_PREVIEW_ID:
            return state.set('currentRenderId', action.renderID)

        case actionTypes.CHANGE_TEXT:
            if (action.key === 'customTitle') {
                return state.set('customTitle', action.text)
            } else {
                return state.set('footerText', action.text)
            }

        case actionTypes.UPDATE_TITLE_SIZE:
            switch (true) {
                case action.titleLength >= XXXL_PDF_HEADER_TITLE_LENGTH:
                    return state.set("titleSize", "title-xxx-long")

                case action.titleLength >= XXL_PDF_HEADER_TITLE_LENGTH:
                    return state.set("titleSize", "title-xx-long")

                case action.titleLength >= XL_PDF_HEADER_TITLE_LENGTH:
                    return state.set("titleSize", "title-x-long")

                case action.titleLength >= LONG_PDF_HEADER_TITLE_LENGTH:
                    return state.set("titleSize", "title-long")

                default:
                    return state.set("titleSize", "")
            }

        case actionTypes.UPDATE_SELECTED_ORGANIZATION_DESIGN_ID:
            return state.set('selectedOrganizationDesignId', action.id)

        case actionTypes.ZOOM_IN:
        case actionTypes.ZOOM_OUT:
            return state.set('zoomLevel', (state.get('zoomLevel') + action.modifyValue))

        case actionTypes.INCREASE_HEIGHT:
        case actionTypes.DECREASE_HEIGHT:
            return state.updateIn(
                    ['elementDetails', action.elementID, 'height'],
                    value => value + action.modifyValue
                )

        case actionTypes.REPLACE_BATCH_SEARCHIFY_FILTERS:
            if (!action.selection) {
                return state
                    .updateIn(
                        ['batchSearchifyFilters'],
                        () => Immutable.fromJS(initialBatchSearchifyFilters()
                    )
                )
            } else {
                // we're taking the input from a Select ([Obj, Obj, Obj, ...]) and
                // converting it into a Searchify Filter.
                return state
                   .setIn(
                        ['batchSearchifyFilters', 'addedIds'],
                        action.selection.map(obj => obj.id)
                    ).setIn(
                        ['batchSearchifyFilters', 'searchifyCount'],
                        action.selection.length
                    )
            }

        case actionTypes.CLEAR_BATCH_SEARCHIFY_FILTERS:
            return state.set('batchSearchifyFilters', Immutable.fromJS(initialBatchSearchifyFilters()))

        case actionTypes.UPDATE_BATCH_SELECT_STATE_NAME:
            return state.set('batchSelectStateName', action.batchSelectStateName)

        case actionTypes.UPDATE_BATCH_SELECT_STATE_FILTERS:
            return state
                    .updateIn(
                        ['batchSearchifyFilters'],
                        () => Immutable.fromJS(action.filter)
                    )

        case actionTypes.UPDATE_ELEMENT_DETAIL:
            return state.updateIn(
                    ['elementDetails', action.elementID, action.detail],
                    () => action.value
                )

        case actionTypes.REPOSITION_ELEMENT:
            const pageNum = String(action.page)
            const oldElementIndex = state.getIn(['pages', pageNum]).indexOf(action.elementID)
            return state.updateIn(
                ['pages', pageNum],
                list => list.delete(oldElementIndex).insert(action.newIndex, action.elementID)
            )

        case actionTypes.UPDATE_SUB_MODE_TYPE:
            return state
                .set('currentSubMode', action.mode)
                .set('currentRenderId', undefined)
                .set('batchSearchifyFilters', Immutable.fromJS(initialBatchSearchifyFilters()))
                .updateIn(
                    ['elementDetails', GLOBAL_DATA_KEY],
                    () => state
                        .withMutations(stateObj =>
                            stateObj.updateIn(
                                ['elementDetails', GLOBAL_DATA_KEY, 'currentRenderId'],
                                () => undefined
                            )
                            .updateIn(
                                ['elementDetails', GLOBAL_DATA_KEY, 'data'],
                                () => undefined
                            )
                            .updateIn(
                                ['elementDetails', GLOBAL_DATA_KEY, 'dataRequest'],
                                () => undefined
                            )
                        )
                        .getIn(["elementDetails", GLOBAL_DATA_KEY])
                )

        case actionTypes.SEARCHIFY_SUBMIT_ELEMENT_FILTER:
            return state
                .updateIn(
                    ['elementDetails', action.elementToFilterID, 'addedFilters'],
                    () => action.filterObj
                )
                .updateIn(
                    ['elementDetails', action.elementToFilterID, 'showOverlay'],
                    () => false
                )
                .updateIn(
                    ['elementDetails', action.elementToFilterID, 'searchTerm'],
                    () => action.searchTerm
                )
                .set('currentPage', DEFAULT_FIRST_PAGE)

        case actionTypes.SEARCHIFY_SUBMIT_BATCH_SELECTOR:
            if (action.filterObj === undefined) { return state }
            return state
                .set('batchSearchifyFilters', Immutable.fromJS(action.filterObj))
                .set('currentPage', DEFAULT_FIRST_PAGE)

        case actionTypes.UPDATE_MODE_TYPE:
            return initialState.withMutations(initialStateObj =>
                initialStateObj
                    .set('currentMode', action.mode)
                    .set('currentSubMode', undefined)
                    .updateIn(
                        ['elementDetails', GLOBAL_DATA_KEY],
                        () => state
                            .withMutations(lastStateObj =>
                                lastStateObj.updateIn(
                                    ['elementDetails', GLOBAL_DATA_KEY, 'currentRenderId'],
                                    () => undefined
                                )
                                .updateIn(
                                    ['elementDetails', GLOBAL_DATA_KEY, 'data'],
                                    () => undefined
                                )
                                .updateIn(
                                    ['elementDetails', GLOBAL_DATA_KEY, 'dataRequest'],
                                    () => undefined
                                )
                                .updateIn(
                                    ['elementDetails', GLOBAL_DATA_KEY, 'topoJsonRegion'],
                                    () => undefined
                                )
                                .updateIn(
                                    ['elementDetails', GLOBAL_DATA_KEY, 'topoJsonRegionCache'],
                                    () => undefined
                                )
                                .updateIn(
                                    ['elementDetails', GLOBAL_DATA_KEY, 'topoJsonRegionCacheRequest'],
                                    () => undefined
                                )
                            )
                            .getIn(["elementDetails", GLOBAL_DATA_KEY])
                    )
            )

        case actionTypes.ADD_PAGE:
            if (action.insertPageNum < 0) {
                // add a new page to the end of the pdf
                const lastPage = +state.get('pages').keySeq().toArray().last() + 1
                return state.updateIn(['pages', `${lastPage}`], () => Immutable.List([]))
            } else {
                // splice in a new, empty page at the correct index
                const pagesJS = state.get('pages').toJS()
                const newPageList = Object.keys(pagesJS).reduce((acc, curr) => (
                    +curr < action.insertPageNum
                        ? Object.assign({}, acc, { [+curr]: pagesJS[+curr] })
                        : Object.assign({}, acc, { [+curr + 1]: pagesJS[+curr] })
                    ), {}
                )
                newPageList[action.insertPageNum] = []
                return state.set('pages', Immutable.fromJS(newPageList))
            }


        case actionTypes.REMOVE_PAGE:
            // - deletes a page
            // - set the new currently selected page to the previous page
            // unless the deleted page is the first page, in which case
            // set it to the new first page
            // - Note: this conditional assumes that the component (rather than
            // the reducer or the pure action creator function) handles the
            // logic to prevent a user from deleting a page if there is
            // only one remaining

            const pageKeys = Object.keys(state.get('pages').toJS())
            const oldPageIndex = pageKeys.indexOf(action.page)

            const newPageID = oldPageIndex === 0
                ? pageKeys[oldPageIndex + 1]
                : pageKeys[oldPageIndex - 1]

            return state
                .deleteIn(['pages', `${action.page}`])
                .set('currentPage', newPageID)

        case actionTypes.ADD_ELEMENT:
            return state.withMutations((stateObj) => {
                stateObj
                    .updateIn(
                        ['elementDetails', `${action.elementID}`],
                        () =>
                            Immutable.Map({
                                displayElement: action.displayElement,
                                componentKey: action.componentKey,
                                elementID: action.elementID,
                            })
                    )
                    .updateIn(
                        ['pages', `${action.page}`],
                        // if there is a specified index, splice the value in
                        // else append to the end
                        // - http://facebook.github.io/immutable-js/docs/#/List/splice
                        list => (
                            action.insertElementIndex >= 0
                                ? list.splice(action.insertElementIndex, 0, action.elementID)
                                : list.push(action.elementID)
                        )
                    )
            })

        case actionTypes.REMOVE_ELEMENT:
            return state.withMutations((stateObj) => {
                stateObj
                    .deleteIn(['elementDetails', `${action.elementID}`])
                    .updateIn(
                        ['pages', `${action.page}`],
                        list => list.splice(list.indexOf(action.elementID), 1)
                    )
            })

        case actionTypes.LOAD_PDF_FROM_TEMPLATE:
            // Need to reset any batch filters
            const newState = Object.assign({},
                action.pdfTemplateJSON,
                { batchSearchifyFilters: initialBatchSearchifyFilters() }
            )

            // Convert from JSON to IMMUTABLE
            return Immutable.fromJS(newState)

        case actionTypes.SET_VIEW_CHOICE:
            return state.set("myFilesViewChoice", action.choice)

        case templateActionTypes.CHANGE_DOWNLOAD_TEMPLATE:
            // Update the currently selected template with the action's value
            return state.updateIn(
                ['elementDetails', 'templateManager', 'data'],
                data =>
                    data.reduce((acc, curr) => {
                        const nextVal = curr.id === state.get('savedTemplateID')
                            ? Object.assign({}, curr, action.updateDict)
                            : curr
                        return acc.concat(nextVal)
                    }, [])
            )

        default:
            return state
    }
}
